import { Bot, ExternalLink, Globe, Building2, Calendar, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getJobQuality, QUALITY_STYLES } from "@/lib/jobQuality";
import { isCrawlerJob, getSourceLabel, getSourceTypeBadge, getCrawlerScore } from "@/lib/jobUtils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ReviewContextSidebarProps {
  post: any;
  meta: any;
  orgName: string;
}

function ScoreBar({ label, value, max = 10 }: { label: string; value: number; max?: number }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium text-foreground">{value}/{max}</span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
}

export function ReviewContextSidebar({ post, meta, orgName }: ReviewContextSidebarProps) {
  const isCrawler = isCrawlerJob(post);
  const quality = getJobQuality(post);
  const qStyle = QUALITY_STYLES[quality.tier];
  const crawlerScore = getCrawlerScore(post);
  const sourceType = meta?.source_type;
  const sourceBadge = sourceType ? getSourceTypeBadge(sourceType) : null;

  return (
    <div className="space-y-4 sticky top-8">
      {/* Organization */}
      <div className="bg-card rounded-2xl p-4 border border-border space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          Organização
        </div>
        <p className="text-sm text-foreground font-medium">{orgName}</p>
        {isCrawler && (
          <Badge variant="secondary" className="gap-1 text-xs">
            <Bot className="w-3 h-3" /> Via Crawler
          </Badge>
        )}
      </div>

      {/* Source info (crawler only) */}
      {isCrawler && meta && (
        <div className="bg-card rounded-2xl p-4 border border-border space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Globe className="w-4 h-4 text-muted-foreground" />
            Fonte
          </div>
          <div className="space-y-2">
            {meta.source_name && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground">{getSourceLabel(meta.source_name)}</span>
                {sourceBadge && (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${sourceBadge.color}`}>
                    {sourceBadge.label}
                  </span>
                )}
              </div>
            )}
            {meta.company_name && (
              <p className="text-xs text-muted-foreground">Empresa: {meta.company_name}</p>
            )}
            <div className="flex flex-col gap-1.5">
              {meta.source_url && (
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-7" asChild>
                  <a href={meta.source_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3 mr-1.5 shrink-0" /> Ver listagem
                  </a>
                </Button>
              )}
              {meta.external_apply_url && (
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-7" asChild>
                  <a href={meta.external_apply_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3 mr-1.5 shrink-0" /> Ver vaga original
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Crawler Score */}
      {isCrawler && crawlerScore != null && (
        <div className="bg-card rounded-2xl p-4 border border-border space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Star className="w-4 h-4 text-muted-foreground" />
            Crawler Score
          </div>
          <div className="flex items-center gap-3">
            <div className={`text-2xl font-bold ${
              crawlerScore >= 7 ? "text-green-600 dark:text-green-400" :
              crawlerScore >= 4 ? "text-amber-600 dark:text-amber-400" :
              "text-red-600 dark:text-red-400"
            }`}>
              {crawlerScore}
            </div>
            <span className="text-xs text-muted-foreground">/ 10</span>
          </div>
          <Progress
            value={crawlerScore * 10}
            className="h-2"
          />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Score automático baseado na completude dos dados coletados pelo crawler.
          </p>
        </div>
      )}

      {/* Quality */}
      <div className="bg-card rounded-2xl p-4 border border-border space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <div className={`w-2.5 h-2.5 rounded-full ${qStyle.dot}`} />
          Qualidade dos Dados
        </div>
        <ScoreBar label="Campos preenchidos" value={quality.filled} max={quality.total} />
        <div className={`text-xs font-semibold px-2 py-1 rounded-lg inline-block ${qStyle.bg} ${qStyle.text}`}>
          {qStyle.label}
        </div>
        {quality.missing.length > 0 && (
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground font-medium">Campos faltantes:</p>
            <div className="flex flex-wrap gap-1">
              {quality.missing.map((m) => (
                <span key={m} className="text-[10px] bg-destructive/10 text-destructive px-1.5 py-0.5 rounded">
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timestamps */}
      <div className="bg-card rounded-2xl p-4 border border-border space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          Datas
        </div>
        <div className="text-xs space-y-1 text-muted-foreground">
          <p>Criada: {format(new Date(post.created_at), "dd MMM yyyy HH:mm", { locale: ptBR })}</p>
          <p>Atualizada: {format(new Date(post.updated_at), "dd MMM yyyy HH:mm", { locale: ptBR })}</p>
          {post.published_at && (
            <p>Publicada: {format(new Date(post.published_at), "dd MMM yyyy HH:mm", { locale: ptBR })}</p>
          )}
        </div>
      </div>
    </div>
  );
}
